// Tiny JWT store. We keep the token in localStorage — acceptable for an
// internal control panel, not for a high-value consumer app. Rotate to an
// httpOnly refresh-token flow if the threat model ever demands it.
//
// A `pugio:auth-changed` CustomEvent is dispatched on set/clear so
// <RequireAuth> and similar components can react without polling. During the
// compatibility window we also dispatch the legacy events and mirror
// localStorage writes to the legacy keys so open tabs and rollback builds keep
// working. We also fall back to an in-memory shim when localStorage throws
// (Safari private mode, disabled storage, some embedded webviews).

const KEY = "pugio.jwt";
const LEGACY_KEYS = ["sol0.jwt", "mcpforge.jwt"] as const;
const AUTH_STORAGE_KEYS = [KEY, ...LEGACY_KEYS] as const;
const EVENT = "pugio:auth-changed";
const LEGACY_EVENTS = ["sol0:auth-changed", "mcpforge:auth-changed"] as const;

// Separate slot for the ``mfa_enrollment_only``-scoped JWT we receive
// when /auth/login returns ``mfa_enrollment_required=true``.  Kept off
// the main KEY so it never gets auto-attached to arbitrary API calls
// via getToken() — the MandatoryMfaPage must opt-in per-call via the
// ``tokenOverride`` RequestOpt.  Cleared after enrolment completes.
const RESTRICTED_KEY = "pugio.jwt.restricted";
const LEGACY_RESTRICTED_KEYS = [
    "sol0.jwt.restricted",
    "mcpforge.jwt.restricted",
] as const;
const RESTRICTED_STORAGE_KEYS = [RESTRICTED_KEY, ...LEGACY_RESTRICTED_KEYS] as const;

let memoryToken: string | null = null;
let memoryRestrictedToken: string | null = null;

function mirrorLocalStorageAliases(keys: readonly string[], value: string): void {
    for (const key of keys) {
        try {
            if (localStorage.getItem(key) !== value) {
                localStorage.setItem(key, value);
            }
        } catch {
            // Best-effort compatibility mirror; callers still keep memory fallback.
        }
    }
}

function getLocalStorageAlias(keys: readonly string[]): string | null {
    for (const key of keys) {
        const stored = localStorage.getItem(key);
        if (stored !== null) return stored;
    }

    return null;
}

function setLocalStorageAliases(keys: readonly string[], value: string): void {
    mirrorLocalStorageAliases(keys, value);
}

function clearLocalStorageAliases(keys: readonly string[]): void {
    for (const key of keys) {
        try {
            localStorage.removeItem(key);
        } catch {
            // memory fallback already cleared
        }
    }
}

function notify(): void {
    for (const event of AUTH_CHANGED_EVENTS) {
        try {
            window.dispatchEvent(new CustomEvent(event));
        } catch {
            // non-browser context (SSR, tests) - nothing to notify
        }
    }
}

export function getToken(): string | null {
    try {
        const stored = getLocalStorageAlias(AUTH_STORAGE_KEYS);
        if (stored !== null) return stored;
    } catch {
        // fall through to memory
    }
    return memoryToken;
}

export function setToken(token: string): void {
    memoryToken = token;
    try {
        setLocalStorageAliases(AUTH_STORAGE_KEYS, token);
    } catch {
        // private-mode / storage disabled — memory fallback already set
    }
    notify();
}

export function clearToken(): void {
    memoryToken = null;
    try {
        clearLocalStorageAliases(AUTH_STORAGE_KEYS);
    } catch {
        // memory fallback already cleared
    }
    notify();
}

export function isAuthed(): boolean {
    return !!getToken();
}

// ---------- Restricted (mfa-enrolment-only) token ----------

export function getRestrictedToken(): string | null {
    try {
        const stored = getLocalStorageAlias(RESTRICTED_STORAGE_KEYS);
        if (stored !== null) return stored;
    } catch {
        // fall through to memory
    }
    return memoryRestrictedToken;
}

export function setRestrictedToken(token: string): void {
    memoryRestrictedToken = token;
    try {
        setLocalStorageAliases(RESTRICTED_STORAGE_KEYS, token);
    } catch {
        // private-mode / storage disabled — memory fallback already set
    }
}

export function clearRestrictedToken(): void {
    memoryRestrictedToken = null;
    try {
        clearLocalStorageAliases(RESTRICTED_STORAGE_KEYS);
    } catch {
        // memory fallback already cleared
    }
}

export const AUTH_CHANGED_EVENT = EVENT;
export const LEGACY_AUTH_CHANGED_EVENT = LEGACY_EVENTS[0];
export const AUTH_CHANGED_EVENTS = [EVENT, ...LEGACY_EVENTS] as const;
